import React, { useState } from "react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import PublicLayout from "@/components/PublicLayout";
import { Mail, Phone, MapPin } from "lucide-react";
import { useLang } from "@/lib/i18n";
import useSEO from "@/lib/useSEO";

export default function Contact() {
  const { t, lang } = useLang();
  useSEO({
    lang, path: "/contatti",
    title: lang === "en" ? "Contact — Il Melograno apARTments" : "Contatti — Il Melograno apARTments",
    description: lang === "en"
      ? "Get in touch with Il Melograno apARTments in Castellanza (VA). Phone: +39 349 5264881. Email: ionnisnc@gmail.com. Book your rooms or lofts."
      : "Contatta Il Melograno apARTments a Castellanza (VA). Telefono: +39 349 5264881. Email: ionnisnc@gmail.com. Prenota camere e loft.",
  });
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", body: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await http.post("/messages", form);
      toast.success(t.contact.success);
      setForm({ name: "", email: "", phone: "", subject: "", body: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || t.detail.errorGeneric);
    } finally {
      setBusy(false);
    }
  };

  return (
    <PublicLayout>
      <section className="mel-container pt-16 pb-24 grid lg:grid-cols-2 gap-16">
        <div>
          <div className="eyebrow mb-4">{t.contact.eyebrow}</div>
          <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl leading-tight tracking-tight break-words">
            {t.contact.titleA} <em className="text-[var(--mel-primary)]">{t.contact.titleB}</em> {t.contact.titleC}
          </h1>
          <p className="mt-6 text-[var(--mel-muted)] text-lg max-w-lg">{t.contact.subtitle}</p>

          <div className="mt-12 space-y-6">
            <div className="flex items-start gap-4">
              <div className="p-3 rounded-md bg-[var(--mel-surface)]"><Mail size={18} className="text-[var(--mel-primary)]" /></div>
              <div>
                <div className="eyebrow mb-1">{t.contact.email}</div>
                <div className="text-sm">ionnisnc@gmail.com</div>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="p-3 rounded-md bg-[var(--mel-surface)]"><Phone size={18} className="text-[var(--mel-primary)]" /></div>
              <div>
                <div className="eyebrow mb-1">{t.contact.phone}</div>
                <div className="text-sm">+39 349 5264881</div>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="p-3 rounded-md bg-[var(--mel-surface)]"><MapPin size={18} className="text-[var(--mel-primary)]" /></div>
              <div>
                <div className="eyebrow mb-1">{t.contact.seat}</div>
                <div className="text-sm">{t.contact.seatValue}</div>
              </div>
            </div>
          </div>
        </div>

        <form onSubmit={submit} className="border border-[var(--mel-border)] rounded-md p-8 md:p-10 bg-[var(--mel-bg)]" data-testid="contact-form">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs eyebrow">{t.detail.yourName}</label>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full mt-1 border border-[var(--mel-border)] rounded-md px-3 py-2 text-sm bg-white" data-testid="contact-name" />
            </div>
            <div>
              <label className="text-xs eyebrow">{t.detail.yourEmail}</label>
              <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full mt-1 border border-[var(--mel-border)] rounded-md px-3 py-2 text-sm bg-white" data-testid="contact-email" />
            </div>
          </div>
          <label className="text-xs eyebrow mt-4 block">{t.detail.phone}</label>
          <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full mt-1 border border-[var(--mel-border)] rounded-md px-3 py-2 text-sm bg-white" data-testid="contact-phone" />
          <label className="text-xs eyebrow mt-4 block">{t.contact.subject}</label>
          <input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="w-full mt-1 border border-[var(--mel-border)] rounded-md px-3 py-2 text-sm bg-white" data-testid="contact-subject" />
          <label className="text-xs eyebrow mt-4 block">{t.contact.body}</label>
          <textarea required rows="6" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="w-full mt-1 border border-[var(--mel-border)] rounded-md px-3 py-2 text-sm bg-white" data-testid="contact-body" />
          <button type="submit" disabled={busy} className="mel-btn-primary mt-6 w-full justify-center" data-testid="contact-submit">
            {busy ? t.common.sending : t.contact.submit}
          </button>
        </form>
      </section>
    </PublicLayout>
  );
}
